import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { Truck, Train, Ship, MapPin, Clock, Shield, CheckCircle, Route } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useState } from "react";
import bannerShipping from "@/assets/banner-shipping.jpg";
import globalNetwork from "@/assets/global-network.jpg";

const Intermodal = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const services = [
    {
      icon: Truck,
      title: "Road Transport",
      description: "Full truckload (FTL) and less than truckload (LTL) services across Europe",
      features: ["FTL & LTL options", "GPS tracking", "Scheduled deliveries", "Cross-border expertise"]
    },
    {
      icon: Train,
      title: "Rail Freight",
      description: "Eco-friendly rail solutions connecting major European corridors",
      features: ["Block trains", "Intermodal wagons", "Reduced carbon footprint", "Cost-effective for bulk"]
    },
    {
      icon: Ship,
      title: "Barge Transport",
      description: "Inland waterway solutions for ports and industrial zones",
      features: ["Container barges", "Bulk cargo", "Port connections", "Heavy lift capability"]
    },
    {
      icon: Route,
      title: "Multi-Modal Solutions",
      description: "Seamless combination of transport modes for optimal efficiency",
      features: ["Single point of contact", "Optimized routing", "Consolidated billing", "End-to-end visibility"]
    }
  ];

  const corridors = [
    { name: "Transpacific Gateway", route: "LA/Long Beach → Chicago → New York", modes: "Port + Rail + Road" },
    { name: "Midwest Express", route: "LA/Long Beach → Dallas → Kansas City → Chicago", modes: "Rail + Road" },
    { name: "Border Corridor", route: "Los Angeles → El Paso → Laredo → Monterrey", modes: "Road + Rail" },
    { name: "West Coast Route", route: "Los Angeles → Oakland → Portland → Seattle → Vancouver", modes: "Road + Rail" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src={bannerShipping} 
          alt="Intermodal Transport" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70" />
        <div className="relative z-10 text-center text-primary-foreground px-4 animate-fade-in">
          <Truck className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Intermodal & Road Transport</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8 opacity-90">
            Seamless multimodal logistics solutions connecting ports to final destinations. Combining road, rail, and waterway transport for maximum efficiency.
          </p>
          <Button size="lg" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
            Get Transport Quote
          </Button>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Connected Logistics Network</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Our intermodal solutions combine the best of road, rail, and waterway transport to deliver your cargo efficiently and sustainably. From the ports of Los Angeles and Long Beach to any destination in North America.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  With our strategic location in California, we serve as the gateway for global trade, offering optimized routing that saves time and reduces costs.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">500+</div>
                    <div className="text-sm text-muted-foreground">Trucks in Network</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">35</div>
                    <div className="text-sm text-muted-foreground">Countries Covered</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">24hr</div>
                    <div className="text-sm text-muted-foreground">Average Transit to EU</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">30%</div>
                    <div className="text-sm text-muted-foreground">CO2 Savings with Rail</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={globalNetwork} 
                  alt="Transport Network" 
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </section>

          {/* Service Types */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Transport Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-muted-foreground mb-6">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 text-primary mr-2" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Corridors */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Major Transport Corridors</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {corridors.map((corridor, index) => (
                <Card key={index} className="hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold">{corridor.name}</h3>
                      <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">{corridor.modes}</span>
                    </div>
                    <div className="flex items-start text-muted-foreground">
                      <MapPin className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{corridor.route}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Vehicle Types */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Fleet</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[
                { type: "Curtainsider", desc: "Flexible loading, weather protection", capacity: "24 tons" },
                { type: "Refrigerated", desc: "Temperature-controlled transport", capacity: "-25°C to +25°C" },
                { type: "Flatbed", desc: "Oversized and heavy cargo", capacity: "30 tons" },
                { type: "Mega Trailer", desc: "High-volume shipments", capacity: "100 m³" },
                { type: "Tanker", desc: "Liquid and bulk materials", capacity: "32,000 liters" },
                { type: "Car Carrier", desc: "Vehicle transportation", capacity: "8-10 vehicles" },
                { type: "Container Chassis", desc: "Port to door container moves", capacity: "20'/40'/45'" },
                { type: "Low Loader", desc: "Heavy machinery transport", capacity: "50+ tons" }
              ].map((vehicle, index) => (
                <Card key={index} className="text-center hover-scale animate-fade-in">
                  <CardContent className="p-6">
                    <Truck className="h-10 w-10 text-primary mx-auto mb-3" />
                    <h3 className="font-bold mb-2">{vehicle.type}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{vehicle.desc}</p>
                    <span className="text-xs bg-secondary px-2 py-1 rounded">{vehicle.capacity}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Benefits of Intermodal</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: Clock, title: "Time Efficiency", desc: "Optimized routing combines the speed of road with the capacity of rail and sea" },
                { icon: Shield, title: "Reduced Risk", desc: "Less handling means fewer chances for damage. Sealed containers travel door-to-door" },
                { icon: MapPin, title: "Sustainability", desc: "Rail and waterway options significantly reduce carbon footprint compared to road-only transport" }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="text-center hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-primary text-primary-foreground rounded-xl p-12 animate-fade-in">
            <h2 className="text-3xl font-bold mb-4">Need Land Transport Solutions?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Whether it's a single truck or a complete multimodal solution, our Athens team will find the best route for your cargo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Request Transport Quote
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Transport Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="intermodal" />
    </div>
  );
};

export default Intermodal;
